import { useCallback, useState } from "react";
import { Play } from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import { formatPreviewTime } from "@shared-core/domain/preview-clock";
import { Badge } from "../../app/components/primitives/badge";
import { Button } from "../../app/components/primitives/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import {
  EmptyRow,
  FailedRow,
  LoadingRow,
  describeError,
  useLoaded,
} from "./consoleState";

/** Jobs (07 §1): the preview's lifecycle jobs, each one's last run, and Run now. */
export function JobsTab() {
  const adapter = useDataAdapter();
  const loadJobs = useCallback(() => adapter.getPreviewJobs(), [adapter]);
  const [jobs, reload] = useLoaded(loadJobs);
  const [running, setRunning] = useState<string | null>(null);

  async function run(names: string[] | undefined, label: string) {
    setRunning(label);
    try {
      const runs = await adapter.runPreviewJobs(names);
      const failed = runs.filter((r) => !r.ok).length;
      toast[failed ? "error" : "success"](
        `${runs.length} job${runs.length === 1 ? "" : "s"} ran${failed ? `, ${failed} failed` : ""}.`,
      );
      reload();
    } catch (err) {
      toast.error(describeError(err));
    } finally {
      setRunning(null);
    }
  }

  if (jobs.status === "loading")
    return <LoadingRow label="Loading the job table…" />;
  if (jobs.status === "failed")
    return <FailedRow message={jobs.message} onRetry={reload} />;
  if (jobs.data.jobs.length === 0)
    return <EmptyRow>This preview runs no jobs.</EmptyRow>;

  const lastRun = new Map(jobs.data.last_runs.map((r) => [r.name, r]));
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button
          size="sm"
          disabled={running !== null}
          onClick={() => void run(undefined, "all")}
        >
          <Play className="size-4" />
          Run every job now
        </Button>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Job</TableHead>
            <TableHead>Last run (preview time)</TableHead>
            <TableHead>Result</TableHead>
            <TableHead className="text-right">Took</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.data.jobs.map((name) => {
            const last = lastRun.get(name);
            return (
              <TableRow key={name}>
                <TableCell className="font-mono">{name}</TableCell>
                <TableCell>
                  {last ? formatPreviewTime(last.at) : "Not run yet"}
                </TableCell>
                <TableCell>
                  {last && (
                    <Badge variant={last.ok ? "outline" : "destructive"}>
                      {last.ok
                        ? "ok"
                        : `failed${last.error_type ? ` (${last.error_type})` : ""}`}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {last ? `${last.duration_ms} ms` : "—"}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={running !== null}
                    onClick={() => void run([name], name)}
                  >
                    Run now
                  </Button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
